import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ListingRow } from '@marstein-radar/shared';
import { formatEur } from '../styles';

const CATEGORY_LABEL: Record<string, string> = {
  nehnutelnosti: 'Nehnuteľnosti',
  stroje_vybavenie: 'Stroje a vybavenie',
  zasoby_tovar: 'Zásoby/tovar',
  firmy_podiely: 'Firmy/podiely',
  vozidla: 'Vozidlá',
  ostatne: 'Ostatné',
};

// Fáza 2 inzertnej sekcie (2026-07-15) — detail jedného inzerátu (plný popis,
// obrázky, kontakt), keďže ListingsFeedPage.tsx karta ukazuje len súhrn.
export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();

  const {
    data: listing,
    isFetching,
    error,
  } = useQuery<ListingRow>({
    queryKey: ['listing', id],
    queryFn: async () => {
      const res = await fetch(`/api/v1/listings/${id}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!id,
  });

  if (isFetching && !listing) return <main className="page">Načítavam…</main>;
  if (error || !listing) {
    return (
      <main className="page">
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          Inzerát nenájdený alebo nie je verejný.
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <h1>{listing.title}</h1>
        <p>{CATEGORY_LABEL[listing.category] ?? listing.category}</p>
      </div>

      {listing.isInsolvencyRelated && (
        <div className="fin-panel" style={{ padding: '14px 20px', marginBottom: '1.5rem' }}>
          Predajca označil tento inzerát ako súvisiaci s insolvenciou. Corpuls{' '}
          <strong>neoveruje</strong> právo predajcu majetok predať — pri majetku z konkurznej
          podstaty môže platiť predpísaný postup speňaženia cez správcu podľa zákona o konkurze a
          reštrukturalizácii. Informujte sa priamo u predajcu/správcu pred kúpou.
        </div>
      )}

      {listing.images.length > 0 && (
        <div className="listing-grid" style={{ marginBottom: '1.5rem' }}>
          {listing.images.map((img) => (
            <img
              key={img.id}
              className="listing-card-image"
              src={`/uploads/${img.filename}`}
              alt={listing.title}
              style={{ borderRadius: 'var(--radius)' }}
            />
          ))}
        </div>
      )}

      <div className="fin-panel" style={{ padding: '18px 22px', marginBottom: '1.5rem' }}>
        <div className="listing-card-price" style={{ fontSize: '1.3rem' }}>
          {listing.priceCents != null ? formatEur(listing.priceCents / 100) : 'Cena dohodou'}
        </div>
        <p style={{ whiteSpace: 'pre-wrap' }}>{listing.description}</p>
        <dl className="profile-facts">
          <dt>Mesto</dt>
          <dd>{listing.city ?? '—'}</dd>
          <dt>Kontakt</dt>
          <dd>
            {listing.contactEmail}
            {listing.contactPhone ? ` · ${listing.contactPhone}` : ''}
          </dd>
          {listing.companyIco && (
            <>
              <dt>Súvisiaca firma</dt>
              <dd>
                <Link to={`/firma/${listing.companyIco}`}>
                  {listing.companyName ?? listing.companyIco}
                </Link>
              </dd>
            </>
          )}
        </dl>
      </div>
    </main>
  );
}
